import { Calendar, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { cn } from '@/shared/utils/cn';
import type { DateRangePreset, PartnerCompany } from '../../types/analytics.types';
import { DATE_RANGE_PRESETS } from '../../types/analytics.types';

const PRESET_LABELS: Record<string, string> = {
  '7d': '7d',
  '30d': '30d',
  '90d': '90d',
  '12m': '12m',
};

type AnalyticsFilterBarProps = {
  dateRange: DateRangePreset;
  onDateRangeChange: (preset: DateRangePreset) => void;
  partnerCompanies: PartnerCompany[];
  selectedPartnerCompanyId: string | null;
  onPartnerCompanyChange: (id: string | null) => void;
  excludeInternal: boolean;
  onExcludeInternalChange: (value: boolean) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  canRefresh: boolean;
  cooldownRemainingMs: number;
};

export const AnalyticsFilterBar = ({
  dateRange,
  onDateRangeChange,
  partnerCompanies,
  selectedPartnerCompanyId,
  onPartnerCompanyChange,
  excludeInternal,
  onExcludeInternalChange,
  onRefresh,
  isRefreshing,
  canRefresh,
  cooldownRemainingMs,
}: AnalyticsFilterBarProps) => {
  const hasInternal = partnerCompanies.some((c) => c.isInternal);
  const cooldownMinutes = Math.ceil(cooldownRemainingMs / 60000);

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <div className="flex rounded-md border border-gray-300 overflow-hidden">
              {DATE_RANGE_PRESETS.filter((p) => p !== 'custom').map(
                (preset) => (
                  <button
                    key={preset}
                    data-active={dateRange === preset ? 'true' : 'false'}
                    onClick={() => onDateRangeChange(preset)}
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium transition-colors',
                      dateRange === preset
                        ? 'bg-[#3d6560] text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50',
                    )}
                  >
                    {PRESET_LABELS[preset] ?? preset}
                  </button>
                ),
              )}
            </div>
          </div>

          {partnerCompanies.length > 0 && (
            <select
              value={selectedPartnerCompanyId ?? ''}
              onChange={(e) =>
                onPartnerCompanyChange(e.target.value || null)
              }
              aria-label="Filter by partner company"
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700"
            >
              <option value="">All Partners</option>
              {partnerCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          {hasInternal && (
            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
              <Checkbox
                checked={excludeInternal}
                onCheckedChange={(checked) =>
                  onExcludeInternalChange(checked === true)
                }
                aria-label="Exclude bQuest Internal"
              />
              Exclude bQuest Internal
            </label>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={!canRefresh}
          aria-label="Refresh data"
          title={
            isRefreshing
              ? 'Refresh in progress...'
              : cooldownRemainingMs > 0
                ? `Available in ${cooldownMinutes}m`
                : 'Refresh data'
          }
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {isRefreshing
            ? 'Refreshing...'
            : cooldownRemainingMs > 0
              ? `Refresh (${cooldownMinutes}m)`
              : 'Refresh'}
        </Button>
      </div>
    </div>
  );
};
